import React from 'react';
import {fromJS, List} from 'immutable';
import moment from 'moment';
import {stats} from 'lib/StatsUtil';
import {pluralize} from 'lib/stringUtils';
import {renderDuration} from 'lib/timeUtils';
import {asOrderedMap} from 'lib/Immutable.utils';
import {makeNavigateToQuestion} from 'client/Classrooms/ClassroomUtil';
import Button from 'sg/Button/Button.react';
import SettingsTable from 'sg/Tables/SettingsTable/SettingsTable.react';
import DownloadCSV from 'sg/Tables/SettingsTable/DownloadCSV.react';
import {Column} from 'sg/Tables/MandarkTable/MandarkTable.react';
import {makeLinkRowProps} from 'sg/Tables/Table/table.utils';
import {sortDirections} from 'sg/Tables/tableConstants';
import FlatBarChart from 'generic/FlatBarChart/FlatBarChart.react';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {getDifficultySpan} from 'lib/questionRenderUtils';
import classroomStore from 'client/Classrooms/Classroom/ClassroomV2.store';
import {RANGES} from '@albert-io/atomic/organisms/DatePicker/DatePicker.helpers';
import {ReportsLinkBanner} from 'client/Reports/ReportsLinkBanner.react';

import classroomInsightsStore from '../ClassroomInsights.store';

const csvColumns = fromJS(
  {
    Questions: 'question_title',
    'Avg Time Spent (HH:MM:SS)': {
      'Avg Time Spent (seconds)': 'question_average_time_spent'
    },
    Accuracy: 'question_average_accuracy',
    'Answer Breakdown': {
      'Correct Attempts': 'question_count_students_answered_correctly',
      'Incorrect Attempts': 'question_count_students_answered_incorrectly',
      Unanswered: 'question_count_students_unanswered'
    },
    'Correct Attempts': 'question_count_students_answered_correctly',
    'Incorrect Attempts': 'question_count_students_answered_incorrectly',
    Unanswered: 'question_count_students_unanswered'
  },
  asOrderedMap
);

export default class QuestionsPerformance extends React.Component {
  getDifficulty = (question) => getDifficultySpan(question.getDifficulty());

  getTitle = (question) => <MarkdownRendererV2 text={question.getTitle()} />;

  getAverageTimeSpent = (question) => {
    const timeSpentDuration = moment.duration(question.getClassroomInsightsAverageTimeSpent());
    return renderDuration(timeSpentDuration, {style: 'hh:mm:ss'});
  };

  getAnswerBreakdown = (question) => {
    const correctGuesses = this.getCorrectAttempts(question);
    const incorrectGuesses = this.getIncorrectAttempts(question);
    const totalGuesses = this.getTotalAttempts(question);
    const unansweredGuesses = this.getUnanswered(question);
    const totalPotentialGuesses = correctGuesses + incorrectGuesses + unansweredGuesses;

    const performanceData = fromJS([
      {
        fillColorClass: 'classroom-insights__performance--correct',
        fillPercentage: stats.percentage(correctGuesses, totalPotentialGuesses),
        keyLabel: 'Correct'
      },
      {
        fillColorClass: 'classroom-insights__performance--incorrect',
        fillPercentage: stats.percentage(incorrectGuesses, totalPotentialGuesses),
        keyLabel: 'Incorrect'
      },
      {
        fillColorClass: 'classroom-insights__performance--unanswered',
        fillPercentage: stats.percentage(unansweredGuesses, totalPotentialGuesses),
        keyLabel: 'Not Answered'
      }
    ]);

    return (
      <div className='usage-report__performance'>
        <small>
          {totalGuesses} {pluralize('student', totalGuesses)} attempted
        </small>
        <FlatBarChart inline data={performanceData} />
      </div>
    );
  };

  getAccuracy = (question) => {
    const correctGuesses = question.getClassroomInsightsCountCorrectStudents();
    const incorrectGuesses = question.getClassroomInsightsCountIncorrectStudents();
    const totalGuesses = correctGuesses + incorrectGuesses;
    return `${stats.percentage(correctGuesses, totalGuesses)}%`;
  };

  getViewQuestionsButton = (question) => {
    const questionSetSlug = question.getQuestionSet().getSlugId();
    const questionSlug = question.getSlugId();
    return (
      <Button
        text='View question'
        whiteBg
        small
        onClick={(e) => {
          e.stopPropagation();
          makeNavigateToQuestion(questionSetSlug, questionSlug)();
        }}
      />
    );
  };

  getTotalAttempts = (question) => {
    const correctGuesses = question.getClassroomInsightsCountCorrectStudents();
    const incorrectGuesses = question.getClassroomInsightsCountIncorrectStudents();
    return correctGuesses + incorrectGuesses;
  };

  getUnanswered = (question) => question.getClassroomInsightsCountUnansweredStudents();

  getCorrectAttempts = (question) => question.getClassroomInsightsCountCorrectStudents();

  getIncorrectAttempts = (question) => question.getClassroomInsightsCountIncorrectStudents();

  getCSVHeaders = (visibleColumns) => {
    const firstColumn = csvColumns.keySeq().first();

    const visibleColumnsWithFirst = visibleColumns.toList().unshift(firstColumn);

    const formattedColumns = csvColumns
      .filter((column, key) => visibleColumnsWithFirst.includes(key))
      .flatten()
      .reduce((acc, value, key) => acc.push(`${value}=${key}`), List());

    return encodeURIComponent(formattedColumns.join(','));
  };

  getQuestionLinkProps() {
    return makeLinkRowProps(
      (question) =>
        `/classes/${classroomStore.getClassroomId()}` +
        `/insights/topic-drilldown/question/${question.getId()}` +
        `?attempt=${classroomInsightsStore.getAttemptNumber()}` +
        `&startDate=${classroomInsightsStore.getStartDate()}` +
        `&endDate=${classroomInsightsStore.getEndDate()}` +
        `&subjectId=${classroomInsightsStore.getSubjectId()}` +
        `&guideLevelId=${classroomInsightsStore.getGuideLevelId()}`
    );
  }

  render() {
    const classroomName = classroomStore.getActiveClassroom().getName();
    const assignmentName = classroomInsightsStore.getAssignmentName();
    const isSubjectReport = classroomInsightsStore.isSubjectReport();
    const csvTitle = isSubjectReport
      ? `${classroomName}-Insights-Questions`
      : `${assignmentName}-Questions`;

    const classroomId = classroomStore.getClassroomId();
    const subjectId = classroomInsightsStore.getSubjectId();
    const attemptNumber = classroomInsightsStore.getAttemptNumber();
    const guideLevelId = classroomInsightsStore.getGuideLevelId();
    const assignmentId = classroomInsightsStore.getAssignmentId();
    const [startDate] = new Date(classroomInsightsStore.getStartDate()).toISOString().split('T');
    const [endDate] = new Date(classroomInsightsStore.getEndDate()).toISOString().split('T');

    const queryParams = new URLSearchParams();
    queryParams.set('startDate', startDate);
    queryParams.set('range', RANGES.CUSTOM);
    queryParams.set('endDate', endDate);
    queryParams.set('attempt', attemptNumber);
    if (classroomInsightsStore.getReportType() === 'subject') {
      queryParams.set('subject', subjectId);
      if (guideLevelId) {
        queryParams.set('guideLevelId', guideLevelId);
      }
    } else {
      queryParams.set('assignmentId', assignmentId);
    }

    return (
      <div>
        <ReportsLinkBanner
          className='u-mar_2'
          link={`/reports/classrooms/${classroomId}/questions?${queryParams.toString()}`}
        />
        <SettingsTable
          query={classroomInsightsStore.getQuestionsPerformanceQuery()}
          rowPropsFunc={isSubjectReport ? this.getQuestionLinkProps() : null}
          storeName='QuestionsPerformanceTableStore'
          searchFilters={classroomInsightsStore.getQuestionsSearchFilters}
          defaultSortDirection={sortDirections.DESCENDING}
          optionsMenu={
            <DownloadCSV
              csvQuery={classroomInsightsStore.getQuestionsPerformanceCSVQuery}
              tableStoreName='QuestionsPerformanceTableStore'
              fileName={csvTitle}
              getCSVHeaders={this.getCSVHeaders}
            />
          }
        >
          <Column defaultVisible align='center' name='Difficulty' content={this.getDifficulty} />
          <Column heading='Questions' sortBy='title' alwaysVisible content={this.getTitle} />
          <Column
            heading='Avg Time Spent (HH:MM:SS)'
            align='right'
            sortBy='meta.classroom_insights_average_time_spent'
            defaultVisible
            content={this.getAverageTimeSpent}
          />
          <Column
            heading='Answer Breakdown'
            align='center'
            sortBy='meta.classroom_insights_count_correct_students'
            defaultSortParam
            defaultVisible
            content={this.getAnswerBreakdown}
          />
          <Column heading='Accuracy' align='right' content={this.getAccuracy} />
          <Column heading='Students Attempted' align='right' content={this.getTotalAttempts} />
          <Column
            heading='Unanswered'
            align='right'
            sortBy='meta.classroom_insights_count_unanswered_students'
            content={this.getUnanswered}
          />
          <Column
            heading='Correct Attempts'
            align='right'
            sortBy='meta.classroom_insights_count_correct_students'
            content={this.getCorrectAttempts}
          />
          <Column
            heading='Incorrect Attempts'
            align='right'
            sortBy='meta.classroom_insights_count_incorrect_students'
            content={this.getIncorrectAttempts}
          />
          <Column alwaysVisible align='center' content={this.getViewQuestionsButton} />
        </SettingsTable>
      </div>
    );
  }
}
